/** The Performance section on an animal's Records tab: weights over time, milestones, scores, scans and carcass. */
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { AnimalDetail } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { formatDate, formatNumber } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { Button, Card, Chip, Disclosure, ErrorNote, Icon, List, Loading, Row, Sheet, Stat } from "../../../web/ui/kit";
import { CODEBOOKS } from "../contract";
import { FORMULAS, RATIO_TEXT } from "../shared/calc";
import type { AnimalPerformance, Placement } from "../shared/dto";
import { MARBLING, QUALITY_GRADES, type Calculated, type ImportedResult, type Milestone, type MilestoneWeight, type PerformanceState } from "../shared/types";
import { checkText, contextLabel, fmtGain, fmtLb, fmtWeight, inUnit, managementLabel, missingText, sexClassLabel, useSystem, weightUnit, type System } from "./fmt";
import { WeightChart } from "./WeightChart";

const AREA = { sq_in: "sq in", sq_cm: "sq cm" } as const;

export function weigh(a: Pick<AnimalDetail, "id" | "label">, data: Record<string, unknown> = {}) {
  openRecord({ initial: { type: "performance.weight", subjects: [{ kind: "animal", id: a.id, role: "animal", label: a.label }], data } });
}

export function measure(a: Pick<AnimalDetail, "id" | "label">) {
  openRecord({ initial: { type: "performance.measurement", subjects: [{ kind: "animal", id: a.id, role: "animal", label: a.label }] } });
}

export function PerformanceSection({ animal }: { animal: AnimalDetail }) {
  useLanguage();
  const system = useSystem();
  const can = useCan();
  const q = useApi<AnimalPerformance>(`/m/performance/animals/${animal.id}`);
  const [explain, setExplain] = useState<{ title: string; calc: Calculated } | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const { state, projected, ratios, checks, dam } = q.data;
  const unit = weightUnit(system);
  const points = (state?.history ?? []).map((p) => ({ on: p.on, value: inUnit(p, system), context: p.context, estimate: p.estimate, flagged: !!checks[p.event_id] }));
  const flagged = (state?.history ?? []).filter((p) => checks[p.event_id]).slice(-3);
  const active = animal.status === "active" || animal.status === "missing";

  return (
    <div className="perf-section stack">
      {state?.last_weight && (
        <div className="perf-stats">
          <Stat label={t("Last weight")} value={fmtWeight(state.last_weight, system)} hint={[formatDate(state.last_weight.on), state.last_weight.estimate ? t("estimated") : null].filter(Boolean).join(" · ")} tone="perf" />
          {state.adg && <Stat label={t("Gain")} value={fmtGain(state.adg, system)} hint={t("since {{date}}", { date: formatDate(state.adg.from) })} />}
          {state.adg_birth && <Stat label={t("Gain since birth")} value={fmtGain(state.adg_birth, system)} hint={plural(state.adg_birth.days, "{{count}} day", "{{count}} days")} />}
          {projected && active && <Stat label={t("Projected today")} value={`~${fmtWeight(projected, system)}`} hint={t("From recent gain. Not weighed.")} />}
        </div>
      )}

      {flagged.map((p) => (
        <div key={p.event_id} className="perf-flag" role="note">
          <Icon name="warn" size={18} />
          <span>{t("{{weight}} on {{date}}:", { weight: fmtWeight(p, system), date: formatDate(p.on) })} {checkText(checks[p.event_id], system)}</span>
          <Link to={`/history/${p.event_id}`} className="link">{t("Open")}</Link>
        </div>
      ))}

      {points.length > 0 && <WeightChart points={points} unit={unit} projected={projected && active ? { on: projected.on, value: inUnit(projected, system) } : null} />}

      {can("record") && active && (
        <div className="quick-actions">
          <Button icon="scale" variant={state ? "secondary" : "primary"} onClick={() => weigh(animal)}>{t("Weigh")}</Button>
          <Button icon="ruler" onClick={() => measure(animal)}>{t("Score or measure")}</Button>
        </div>
      )}

      {state && <Milestones state={state} ratios={ratios} system={system} onExplain={(title, calc) => setExplain({ title, calc })} />}
      {state && <Scores state={state} system={system} onExplain={(title, calc) => setExplain({ title, calc })} />}
      {state?.ultrasound && <UltrasoundCard u={state.ultrasound} system={system} />}
      {state?.carcass && <CarcassCard c={state.carcass} system={system} />}
      {dam && <DamCard dam={dam} system={system} />}

      {!state && !dam && <p className="muted">{t("No weights or scores yet.")}</p>}

      {(state?.history.length ?? 0) > 0 && (
        <Disclosure label={t("Every weight")} count={state!.history.length}>
          <List>
            {[...state!.history].reverse().map((p) => (
              <Row key={p.event_id} icon="scale" tone={checks[p.event_id] ? "warn" : "perf"} to={`/history/${p.event_id}`}
                title={<span className="perf-row-title">{fmtWeight(p, system)}{p.estimate && <Chip tone="neutral">{t("estimated")}</Chip>}{checks[p.event_id] && <Chip tone="warn" icon="warn">{t("Check")}</Chip>}</span>}
                meta={`${formatDate(p.on)} · ${contextLabel(p.context)}`} />
            ))}
          </List>
        </Disclosure>
      )}
      <ExplainSheet value={explain} onClose={() => setExplain(null)} system={system} />
    </div>
  );
}

function Milestones({ state, ratios, system, onExplain }: { state: PerformanceState; ratios: AnimalPerformance["ratios"]; system: System; onExplain: (title: string, c: Calculated) => void }) {
  const rows = (["birth", "weaning", "yearling"] as Milestone[]).filter((m) => state.weights[m] || state.imported?.[m]);
  if (!rows.length) return null;
  return (
    <Card className="perf-milestones">
      {rows.map((m) => (
        <MilestoneRow key={m} stage={m} w={state.weights[m]} imported={state.imported?.[m]} placement={m === "birth" ? null : ratios[m]} system={system} onExplain={onExplain} />
      ))}
    </Card>
  );
}

function MilestoneRow({ stage, w, imported, placement, system, onExplain }: { stage: Milestone; w?: MilestoneWeight; imported?: ImportedResult; placement: Placement | null; system: System; onExplain: (title: string, c: Calculated) => void }) {
  const title = stage === "birth" ? t("Birth weight") : stage === "weaning" ? t("Weaning weight") : t("Yearling weight");
  const adjustedTitle = stage === "weaning" ? t("Adjusted 205-day weight") : t("Adjusted 365-day weight");
  return (
    <div className="perf-milestone">
      <div className="perf-milestone-head">
        <span className="perf-milestone-name">{title}</span>
        {w ? <span className="perf-milestone-value">{fmtWeight(w, system)}</span> : <span className="muted">{t("Not weighed")}</span>}
      </div>
      {w && (
        <p className="perf-milestone-meta">
          {[formatDate(w.on), w.adjusted?.age_days !== undefined ? plural(w.adjusted.age_days, "{{count}} day old", "{{count}} days old") : w.age_adjusted?.age_days !== undefined ? plural(w.age_adjusted.age_days, "{{count}} day old", "{{count}} days old") : null,
            w.method === "estimate" ? t("estimated") : w.method === "tape" ? t("weight tape") : null, w.source === "calving" ? t("from the calving record") : null].filter(Boolean).join(" · ")}
        </p>
      )}
      {w?.adjusted && (
        <div className="perf-derived">
          <span>{adjustedTitle}</span>
          <strong>{fmtLb(w.adjusted.value, system)}</strong>
          <Chip tone="info" icon="sparkle">{t("Calculated")}</Chip>
          {placement?.ratio !== null && placement?.ratio !== undefined && <Chip tone="perf">{t("Ratio {{ratio}}", { ratio: Math.round(placement.ratio) })}</Chip>}
          <button type="button" className="link" onClick={() => onExplain(adjustedTitle, w.adjusted!)}>{t("How it's worked out")}</button>
        </div>
      )}
      {placement && (
        <p className="perf-milestone-meta">
          {placement.ratio === null
            ? t("Only animal in its group, so there's nothing to compare with.")
            : t("{{rank}} of {{count}} {{sex}}, {{group}}; group average {{mean}}", { rank: placement.rank, count: placement.count, sex: sexClassLabel(placement.sex_class).toLowerCase(), group: managementLabel(placement.management), mean: fmtLb(placement.mean, system) })}
        </p>
      )}
      {w && !w.adjusted && w.age_adjusted && stage !== "birth" && (
        <div className="perf-derived">
          <span>{t("Age-adjusted only")}</span>
          <strong>{fmtLb(w.age_adjusted.value, system)}</strong>
          <Chip tone="info" icon="sparkle">{t("Calculated")}</Chip>
          <button type="button" className="link" onClick={() => onExplain(t("Age-adjusted only"), w.age_adjusted!)}>{t("How it's worked out")}</button>
        </div>
      )}
      {w?.missing && stage !== "birth" && <p className="perf-missing"><Icon name="info" size={14} />{t("No adjusted weight yet:")} {missingText(w.missing).join("; ")}</p>}
      {imported && (
        <div className="perf-derived perf-imported">
          <span>{imported.by ? t("Adjusted by {{source}}", { source: imported.by }) : t("Adjusted, from another source")}</span>
          <strong>{fmtWeight(imported, system)}</strong>
          <Chip tone="neutral" icon="upload">{t("Imported")}</Chip>
          {imported.ratio !== undefined && <Chip tone="neutral">{t("Ratio {{ratio}}", { ratio: imported.ratio })}</Chip>}
        </div>
      )}
    </div>
  );
}

function scoreText(kind: keyof typeof CODEBOOKS, value: number): string {
  const label = CODEBOOKS[kind][value];
  return label ? `${formatNumber(value, 1)} · ${t(label)}` : formatNumber(value, 1);
}

function Scores({ state, system, onExplain }: { state: PerformanceState; system: System; onExplain: (title: string, c: Calculated) => void }) {
  const s = state.scores;
  const rows: Array<{ key: string; label: string; value: string; on: string; extra?: ReactNode }> = [];
  if (state.bcs) rows.push({ key: "bcs", label: t("Body condition"), value: scoreText("bcs", state.bcs.score), on: state.bcs.on });
  if (s.scrotal) rows.push({ key: "scrotal", label: t("Scrotal circumference"), value: fmtWeight(s.scrotal, system), on: s.scrotal.on });
  if (s.hip_height) {
    rows.push({
      key: "hip", label: t("Hip height"), value: fmtWeight(s.hip_height, system), on: s.hip_height.on,
      extra: s.frame_calc ? <button type="button" className="link" onClick={() => onExplain(t("Frame score"), s.frame_calc!)}>{t("Frame {{score}}, calculated", { score: formatNumber(s.frame_calc.value, 1) })}</button> : null,
    });
  }
  if (s.frame) rows.push({ key: "frame", label: t("Frame score"), value: formatNumber(s.frame.value, 1), on: s.frame.on });
  if (s.docility) rows.push({ key: "docility", label: t("Docility score"), value: scoreText("docility", s.docility.value), on: s.docility.on });
  if (s.udder) rows.push({ key: "udder", label: t("Udder suspension"), value: scoreText("udder", s.udder.value), on: s.udder.on });
  if (s.teat) rows.push({ key: "teat", label: t("Teat size"), value: scoreText("teat", s.teat.value), on: s.teat.on });
  if (s.foot_angle) rows.push({ key: "foot", label: t("Foot angle"), value: formatNumber(s.foot_angle.value, 0), on: s.foot_angle.on });
  if (s.claw_set) rows.push({ key: "claw", label: t("Claw set"), value: formatNumber(s.claw_set.value, 0), on: s.claw_set.on });
  if (s.muscle) rows.push({ key: "muscle", label: t("Muscle score"), value: formatNumber(s.muscle.value, 0), on: s.muscle.on });
  if (s.pelvic_area) rows.push({ key: "pelvic", label: t("Pelvic area"), value: t("{{value}} cm²", { value: formatNumber(s.pelvic_area.value, 0) }), on: s.pelvic_area.on });
  if (!rows.length) return null;
  return (
    <Card>
      <h3 className="perf-card-title">{t("Scores and measurements")}</h3>
      <dl className="data-list">
        {rows.map((r) => (
          <div key={r.key}><dt>{r.label}</dt><dd><strong className={r.key === "bcs" && state.bcs!.score <= 4 ? "perf-warn" : undefined}>{r.value}</strong> <span className="muted">· {formatDate(r.on)}</span>{r.extra && <> · {r.extra}</>}</dd></div>
        ))}
      </dl>
    </Card>
  );
}

function UltrasoundCard({ u, system }: { u: NonNullable<PerformanceState["ultrasound"]>; system: System }) {
  return (
    <Card>
      <h3 className="perf-card-title">{t("Ultrasound")} <span className="muted">· {formatDate(u.on)}</span></h3>
      <dl className="data-list">
        {u.imf !== undefined && <div><dt>{t("Intramuscular fat (IMF)")}</dt><dd>{formatNumber(u.imf, 2)}%{u.imf_ratio !== undefined && <span className="muted"> · {t("ratio {{ratio}}", { ratio: u.imf_ratio })}</span>}</dd></div>}
        {u.rea && <div><dt>{t("Ribeye area")}</dt><dd>{formatNumber(u.rea.value, 1)} {t(AREA[u.rea.unit])}{u.rea_ratio !== undefined && <span className="muted"> · {t("ratio {{ratio}}", { ratio: u.rea_ratio })}</span>}</dd></div>}
        {u.rib_fat && <div><dt>{t("Rib fat")}</dt><dd>{fmtWeight(u.rib_fat, system)}{u.fat_ratio !== undefined && <span className="muted"> · {t("ratio {{ratio}}", { ratio: u.fat_ratio })}</span>}</dd></div>}
        {u.rump_fat && <div><dt>{t("Rump fat")}</dt><dd>{fmtWeight(u.rump_fat, system)}</dd></div>}
        {u.scan_weight && <div><dt>{t("Scan weight")}</dt><dd>{fmtWeight(u.scan_weight, system)}</dd></div>}
      </dl>
      <p className="field-hint">{t("A scan of the live animal, not a carcass measurement.")}</p>
    </Card>
  );
}

function CarcassCard({ c, system }: { c: NonNullable<PerformanceState["carcass"]>; system: System }) {
  return (
    <Card>
      <h3 className="perf-card-title">{t("Carcass")} <span className="muted">· {formatDate(c.on)}</span></h3>
      <dl className="data-list">
        {c.quality_grade && <div><dt>{t("Quality grade")}</dt><dd>{t(QUALITY_GRADES[c.quality_grade as keyof typeof QUALITY_GRADES] ?? c.quality_grade)}</dd></div>}
        {c.yield_grade !== undefined && <div><dt>{t("Yield grade")}</dt><dd>{formatNumber(c.yield_grade, 1)}</dd></div>}
        {c.hcw && <div><dt>{t("Hot carcass weight")}</dt><dd>{fmtWeight(c.hcw, system)}</dd></div>}
        {c.marbling && <div><dt>{t("Marbling")}</dt><dd>{t(MARBLING[c.marbling as keyof typeof MARBLING] ?? c.marbling)}</dd></div>}
        {c.rea && <div><dt>{t("Ribeye area")}</dt><dd>{formatNumber(c.rea.value, 1)} {t(AREA[c.rea.unit])}</dd></div>}
        {c.back_fat && <div><dt>{t("Back fat")}</dt><dd>{fmtWeight(c.back_fat, system)}</dd></div>}
        {c.plant && <div><dt>{t("Plant")}</dt><dd>{c.plant}</dd></div>}
      </dl>
    </Card>
  );
}

function DamCard({ dam, system }: { dam: NonNullable<AnimalPerformance["dam"]>; system: System }) {
  return (
    <Card>
      <h3 className="perf-card-title">{t("Her calves")}</h3>
      <div className="perf-stats">
        <Stat label={t("MPPA")} value={dam.mppa !== null ? formatNumber(dam.mppa, 1) : "—"} hint={dam.qualifying ? plural(dam.qualifying, "from {{count}} calf ratio", "from {{count}} calf ratios") : t("No calf ratios yet")} tone="perf" />
        <Stat label={t("Calves weaned")} value={`${dam.weaned} / ${dam.offspring}`} hint={dam.average_weaning_lb !== null ? t("average {{weight}}", { weight: fmtLb(dam.average_weaning_lb, system) }) : undefined} />
        {dam.average_ratio !== null && <Stat label={t("Average weaning ratio")} value={formatNumber(dam.average_ratio, 0)} />}
      </div>
      <List>
        {dam.calves.map((c) => (
          <Row key={c.animal_id} icon="calf" to={`/cattle/${c.animal_id}?tab=records`} title={c.label}
            meta={[c.birth_date ? formatDate(c.birth_date) : null, c.weaned ? t("weaned {{weight}}", { weight: fmtWeight(c.weaned, system) }) : t("not weaned yet"),
              c.ratio !== null ? t("ratio {{ratio}}", { ratio: Math.round(c.ratio) }) : c.reason === "single" ? t("no one to compare with") : null].filter(Boolean).join(" · ")} />
        ))}
      </List>
      <p className="field-hint">{t("MPPA compares cows in this herd by their calves' weaning ratios. 100 is average.")}</p>
    </Card>
  );
}

function ExplainSheet({ value, onClose, system }: { value: { title: string; calc: Calculated } | null; onClose: () => void; system: System }) {
  if (!value) return null;
  const { calc } = value;
  const f = FORMULAS[calc.formula];
  const i = calc.inputs;
  const rows: Array<[string, string]> = [];
  if (i.birth_weight) rows.push([t("Birth weight"), `${fmtWeight(i.birth_weight, system)}${i.birth_weight.assumed ? ` · ${t("assumed, from your settings")}` : i.birth_weight.on ? ` · ${formatDate(i.birth_weight.on)}` : ""}`]);
  if (i.birth_date) rows.push([t("Born"), formatDate(i.birth_date)]);
  if (i.weaning_weight) rows.push([t("Weaning weight"), `${fmtWeight(i.weaning_weight, system)} · ${formatDate(i.weaning_weight.on)}`]);
  if (i.weaning_adjusted !== undefined) rows.push([t("Adjusted 205-day weight"), fmtLb(i.weaning_adjusted, system)]);
  if (i.yearling_weight) rows.push([t("Yearling weight"), `${fmtWeight(i.yearling_weight, system)} · ${formatDate(i.yearling_weight.on)}`]);
  if (i.days_between !== undefined) rows.push([t("Days from weaning to yearling"), formatNumber(i.days_between, 0)]);
  if (i.age_days !== undefined) rows.push([t("Age in days"), formatNumber(i.age_days, 0)]);
  if (i.dam_age_years !== undefined) rows.push([t("Dam's age at calving"), plural(i.dam_age_years, "{{count}} year", "{{count}} years")]);
  if (i.aod_lb !== undefined) rows.push([t("Age-of-dam adjustment"), `+${fmtLb(i.aod_lb, system)}`]);
  if (i.hip_height_in !== undefined) rows.push([t("Hip height"), t("{{value}} in", { value: formatNumber(i.hip_height_in, 1) })]);
  if (i.frame_sex) rows.push([t("Equation"), i.frame_sex === "bull" ? t("Bulls") : t("Heifers")]);
  return (
    <Sheet open onClose={onClose} title={value.title}>
      <div className="stack">
        <p className="perf-explain-value">{calc.unit === "lb" ? fmtLb(calc.value, system) : formatNumber(calc.value, 1)} <Chip tone="info" icon="sparkle">{t("Calculated")}</Chip></p>
        <div><strong>{t(f.label)}</strong><p className="muted">{t(f.text)}</p></div>
        <dl className="data-list">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        <p className="field-hint">{t("Rule version {{version}}. Worked out {{date}} from the records above; it changes when they're corrected.", { version: calc.version, date: formatDate(calc.computed_on) })}</p>
        {calc.formula !== "bif_frame" && <p className="field-hint">{t("Ratios compare adjusted weights within a contemporary group: same management, same sex, born within 90 days.")} {t(RATIO_TEXT)}</p>}
      </div>
    </Sheet>
  );
}
